import { useCallback, useState } from 'react';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type StoreActionApi,
	createStore,
	createHook,
	createActionsHook,
} from '@atlassian/react-sweet-state';

type Trait = {
	name: string;
	value: string | boolean;
};

export type UserTraitsState = {
	attributes: Trait[] | null;
};

type StoreAPI = {
	setState: (state: UserTraitsState) => void;
	getState: () => UserTraitsState;
};

const setUserTraits =
	(newTraits: Trait[] | null) =>
	({ getState, setState }: StoreAPI): void => {
		const currentUserTraits = getState();

		setState({
			...currentUserTraits,
			attributes: newTraits,
		});
	};

const addUserTrait =
	(trait: Trait) =>
	({ getState, setState }: StoreAPI): void => {
		const currentUserTraits = getState();

		if (currentUserTraits.attributes?.some((t) => t.name === trait.name)) {
			return;
		}

		setState({
			attributes: currentUserTraits.attributes?.concat(trait) ?? [trait],
		});
	};

const fetchUserTraits =
	(cloudId: string, accountId: string | null) =>
	async ({ getState, setState }: StoreActionApi<UserTraitsState>) => {
		if (!accountId) {
			return null;
		}

		const state = getState();

		if (state.attributes !== null) {
			return;
		}

		const getUserTraits = async () => {
			try {
				const userTraitsData = await performGetRequest(
					`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${accountId}`,
				);
				return userTraitsData?.attributes ?? [];
			} catch {
				return null;
			}
		};

		const userTraits = await getUserTraits();
		setState({ attributes: userTraits });
	};

const actions = { setUserTraits, fetchUserTraits, addUserTrait };

const initialState: UserTraitsState = {
	attributes: null,
};

export const useFetchUserTraitsOld = () => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const [loading, setLoading] = useState<boolean>(false);

	return useCallback(async () => {
		if (!accountId) {
			return null;
		}

		const url = `/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${accountId}`;

		const getUserTraits = async () => {
			try {
				setLoading(true);
				const userTraitsData = await performGetRequest(url);

				// If a user has no attributes on their account, return null so we know
				// not to keep trying to fetch data.
				return !userTraitsData?.attributes?.length ? null : userTraitsData?.attributes;
			} catch {
				return null;
			} finally {
				setLoading(false);
			}
		};
		if (!loading) {
			const userTraits = await getUserTraits();
			return userTraits;
		}
		return false;
	}, [accountId, cloudId, loading]);
};

export const UserTraitsStore = createStore({
	initialState,
	actions,
	name: 'UserTraits',
});

export const useUserTraits = createHook(UserTraitsStore);
export const useUserTraitsActions = createActionsHook(UserTraitsStore);
